import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { facturasService } from '../services/facturasService';
import { siniestrosService } from '../services/siniestrosService';

const fmt = (v) =>
  Number(v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function TotalBox({ label, valor, colorClass }) {
  return (
    <div className={`rounded-xl px-4 py-3 text-center border ${colorClass}`}>
      <p className="text-xs font-medium opacity-60 mb-0.5">{label}</p>
      <p className="text-lg font-bold">{fmt(valor)} €</p>
    </div>
  );
}

function BuscarSiniestro({ onAñadir, isLoading }) {
  const [query, setQuery]         = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando]   = useState(false);
  const [error, setError]         = useState(null);

  const handleBuscar = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBuscando(true);
    setError(null);
    try {
      const res = await siniestrosService.buscar('siniestro', query.trim(), 0, 10);
      setResultados(res.data.content || []);
      if ((res.data.content || []).length === 0) setError('No se encontró ningún siniestro');
    } catch {
      setError('Error al buscar');
    } finally {
      setBuscando(false);
    }
  };

  const handleSeleccionar = (s) => {
    onAñadir(s.id);
    setQuery('');
    setResultados([]);
    setError(null);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-700">Añadir siniestro</h3>
      <form onSubmit={handleBuscar} className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Nº de siniestro..."
          className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={buscando || isLoading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl
                     text-sm font-medium transition disabled:opacity-50"
        >
          {buscando ? '...' : 'Buscar'}
        </button>
      </form>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {resultados.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
          {resultados.map(s => (
            <button
              key={s.id}
              onClick={() => handleSeleccionar(s)}
              disabled={isLoading}
              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition
                         text-sm disabled:opacity-50"
            >
              <span className="font-semibold text-blue-700">{s.nSiniestro || `#${s.id}`}</span>
              {s.extension > 1 && (
                <span className="ml-1 text-xs text-purple-600">Ext.{s.extension}</span>
              )}
              <span className="ml-2 text-gray-600">{s.titular}</span>
              <span className="ml-2 text-gray-400 text-xs">{s.domicilio}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FacturaDetalle() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [factura, setFactura]     = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(false);
  const [facturando, setFacturando] = useState(false);
  const [numInput, setNumInput]         = useState('');
  const [numError, setNumError]         = useState(null);
  const [guardandoNum, setGuardandoNum] = useState(false);

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await facturasService.obtener(id);
      setFactura(res.data);
    } catch {
      setError('Error al cargar la factura');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (factura) setNumInput(String(factura.numFactura ?? ''));
  }, [factura?.numFactura]);

  const numDirty = factura && parseInt(numInput, 10) !== factura.numFactura;

  const handleGuardarNum = async () => {
    const n = parseInt(numInput, 10);
    if (isNaN(n) || n <= 0) {
      setNumError('Debe ser un número positivo');
      return;
    }
    if (n > 9_999_999) {
      setNumError('Máximo 9.999.999');
      return;
    }
    setNumError(null);
    setGuardandoNum(true);
    try {
      await facturasService.actualizarNumFactura(id, n);
      await cargar();
    } catch (e) {
      setNumError(e?.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardandoNum(false);
    }
  };

  const handleAñadirSiniestro = async (idSiniestro) => {
    setIsLoading(true);
    try {
      await facturasService.añadirSiniestro(id, idSiniestro);
      await cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al añadir el siniestro');
      setIsLoading(false);
    }
  };

  const handleEliminarLinea = async (idLinea) => {
    setIsLoading(true);
    try {
      await facturasService.eliminarLinea(idLinea);
      await cargar();
    } catch {
      setError('Error al eliminar la línea');
      setIsLoading(false);
    }
  };

  const handleEliminarFactura = async () => {
    setIsLoading(true);
    try {
      await facturasService.eliminar(id);
      navigate('/facturas');
    } catch {
      setError('Error al eliminar la factura');
      setIsLoading(false);
    }
  };

  const handleFactura = async () => {
    setFacturando(true);
    try {
      await facturasService.cobrar(id);
      navigate(`/facturas/${id}/pdf`);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al generar la factura');
      setFacturando(false);
    }
  };

  if (isLoading && !factura) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"/>
      </div>
    );
  }

  if (!factura) return null;

  const todasCobradas = factura.lineas.length > 0 &&
    factura.lineas.every(l => l.cobrado);

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">

      {/* ── Cabecera ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/facturas')}
          className="text-gray-400 hover:text-gray-600 transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          {/* Nº factura editable */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-gray-500 shrink-0">Factura</span>
            <div className="flex items-center gap-1.5">
              <span className="text-base font-bold text-blue-700">#</span>
              <input
                type="number"
                value={numInput}
                onChange={e => { setNumInput(e.target.value); setNumError(null); }}
                onKeyDown={e => { if (e.key === 'Enter') handleGuardarNum(); }}
                min="1"
                max="9999999"
                className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-base font-bold
                           text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500
                           focus:border-blue-400"
              />
              {numDirty && (
                <button
                  onClick={handleGuardarNum}
                  disabled={guardandoNum}
                  className="min-h-[36px] bg-blue-600 text-white text-xs font-semibold
                             px-3 py-1.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {guardandoNum ? '...' : 'Guardar'}
                </button>
              )}
            </div>
            {todasCobradas && (
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-800
                               text-xs font-semibold px-2.5 py-1 rounded-full">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Cobrada
              </span>
            )}
          </div>
          {numError && (
            <p className="text-xs text-red-600 mt-0.5 ml-1">{numError}</p>
          )}
          <p className="text-xs text-gray-400 mt-0.5">
            {factura.fecha ? new Date(factura.fecha).toLocaleDateString('es-ES', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            }) : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Botón Factura: cobra todos + abre PDF (solo cuando no están todos cobrados) */}
          {!todasCobradas && (
            <button
              onClick={handleFactura}
              disabled={facturando || factura.lineas.length === 0}
              className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700
                         text-white text-sm font-semibold px-3 py-2 rounded-xl
                         transition disabled:opacity-40 shadow-sm"
              title="Cobrar todos los siniestros e imprimir la factura"
            >
              {facturando ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                </svg>
              )}
              <span className="hidden sm:inline">Factura</span>
            </button>
          )}

          {/* Botón Ver PDF: solo cuando ya está cobrada */}
          {todasCobradas && (
            <button
              onClick={() => navigate(`/facturas/${id}/pdf`)}
              className="flex items-center gap-1.5 border border-gray-300 text-gray-700
                         hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-xl transition"
              title="Ver e imprimir factura en PDF"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
              <span className="hidden sm:inline">Ver PDF</span>
            </button>
          )}

          {/* Botón eliminar factura */}
          {!confirmEliminar ? (
            <button
              onClick={() => setConfirmEliminar(true)}
              className="flex items-center gap-1.5 border border-red-300 text-red-600
                         hover:bg-red-50 text-sm font-medium px-3 py-2 rounded-xl transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              <span className="hidden sm:inline">Eliminar</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600 font-medium">¿Seguro?</span>
              <button onClick={handleEliminarFactura}
                className="bg-red-600 text-white text-xs font-semibold px-3 py-2 rounded-xl hover:bg-red-700 transition">
                Sí
              </button>
              <button onClick={() => setConfirmEliminar(false)}
                className="border border-gray-300 text-gray-600 text-xs font-semibold px-3 py-2 rounded-xl hover:bg-gray-50 transition">
                No
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Totales ── */}
      <div className="grid grid-cols-3 gap-3">
        <TotalBox label="Neto"  valor={factura.importeTotal}
                  colorClass="bg-blue-50 border-blue-200 text-blue-800"  />
        <TotalBox label="IVA"   valor={factura.ivaTotal}
                  colorClass="bg-amber-50 border-amber-200 text-amber-800" />
        <TotalBox label="Total" valor={factura.totalTotal}
                  colorClass="bg-green-50 border-green-200 text-green-800" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

{/* ── Buscador para añadir siniestros ── */}
      <BuscarSiniestro onAñadir={handleAñadirSiniestro} isLoading={isLoading} />

      {/* ── Tabla de líneas desktop ── */}
      {factura.lineas.length > 0 && (
        <>
          <div className="hidden sm:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Nº Siniestro</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Titular</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Póliza</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Fecha</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Neto</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">IVA</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Total</th>
                  <th className="px-3 py-3"/>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {factura.lineas.map(l => (
                  <tr key={l.id} className="hover:bg-gray-50 transition group">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/siniestros/${l.codSiniestro}`)}
                        className="font-semibold text-blue-700 hover:underline"
                      >
                        {l.nSiniestro || `#${l.codSiniestro}`}
                      </button>
                      {l.extension > 1 && (
                        <span className="ml-1 text-xs text-purple-600">
                          {'*'.repeat(l.extension - 1)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[160px] truncate">{l.titular}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{l.codPoliza}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {l.fechaAsignacion
                        ? new Date(l.fechaAsignacion).toLocaleDateString('es-ES')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmt(l.importe)} €</td>
                    <td className="px-4 py-3 text-right text-amber-600">{fmt(l.iva)} €</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-700">{fmt(l.total)} €</td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => handleEliminarLinea(l.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-400
                                   hover:text-red-600 transition p-1 rounded-lg hover:bg-red-50"
                        title="Quitar de la factura"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-gray-600">
                    Total ({factura.lineas.length} siniestros)
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">{fmt(factura.importeTotal)} €</td>
                  <td className="px-4 py-3 text-right font-bold text-amber-700">{fmt(factura.ivaTotal)} €</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700 text-base">{fmt(factura.totalTotal)} €</td>
                  <td/>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Cards móvil */}
          <div className="sm:hidden space-y-2">
            {factura.lineas.map(l => (
              <div key={l.id}
                   className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <button
                      onClick={() => navigate(`/siniestros/${l.codSiniestro}`)}
                      className="font-bold text-blue-700 text-sm hover:underline"
                    >
                      {l.nSiniestro || `#${l.codSiniestro}`}
                      {l.extension > 1 && (
                        <span className="ml-1 text-purple-500 text-xs">
                          {'*'.repeat(l.extension - 1)}
                        </span>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-0.5">{l.titular}</p>
                    <p className="text-xs text-gray-400">
                      {l.codPoliza} ·{' '}
                      {l.fechaAsignacion
                        ? new Date(l.fechaAsignacion).toLocaleDateString('es-ES')
                        : '—'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleEliminarLinea(l.id)}
                    className="text-red-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-blue-500 font-medium">Neto</p>
                    <p className="text-sm font-bold text-blue-800">{fmt(l.importe)} €</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-amber-500 font-medium">IVA</p>
                    <p className="text-sm font-bold text-amber-800">{fmt(l.iva)} €</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-green-500 font-medium">Total</p>
                    <p className="text-sm font-bold text-green-800">{fmt(l.total)} €</p>
                  </div>
                </div>
              </div>
            ))}
            {/* Total móvil */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <p className="text-xs text-gray-400 mb-2 font-medium">
                TOTAL FACTURA · {factura.lineas.length} siniestros
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center">
                  <p className="text-xs text-blue-400">Neto</p>
                  <p className="text-sm font-bold text-white">{fmt(factura.importeTotal)} €</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-amber-400">IVA</p>
                  <p className="text-sm font-bold text-white">{fmt(factura.ivaTotal)} €</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-green-400">Total</p>
                  <p className="text-lg font-black text-green-400">{fmt(factura.totalTotal)} €</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {factura.lineas.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200
                        p-10 text-center text-gray-400">
          <p className="font-medium">Factura vacía</p>
          <p className="text-sm mt-1">Busca y añade siniestros usando el campo de arriba</p>
        </div>
      )}
    </div>
  );
}
